/**
 * A stable per-browser identifier for a guest's phone — SPEC §4.1's `guest-device.deviceId`
 * (the server hashes it before ever storing it: `strapi-template/src/utils/device-id.js`), which
 * AC-REQ-6's per-device block/quota needs to "survive a page reload and a new browser tab" — plus
 * the `singerName` `localStorage` remembers between visits (AC-REQ-4: "enter singer name
 * (remembered in localStorage)"). Same localStorage-with-in-memory-fallback shape as
 * `api/client.ts`'s token storage (that module's own storage helpers are private, so this is a
 * small, deliberate duplication rather than a coupling between two otherwise-independent modules —
 * see `api/guest.ts`'s own doc comment on why the guest path stays decoupled from the operator's
 * `api/client.ts` entirely).
 */

const DEVICE_ID_KEY = 'mvcp.deviceId';
const SINGER_NAME_KEY = 'mvcp.singerName';
const GUEST_PASS_KEY = 'mvcp.guestPass';

// localStorage exists in every real browser but not under `node --test` (no --experimental flag
// enables it there) — see api/client.ts's own comment. Falling back to an in-memory Map keeps this
// module correct AND trivially unit-testable in Node with no mocking.
const memoryStore = new Map<string, string>();

function hasLocalStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

function storageGet(key: string): string | null {
  if (hasLocalStorage()) {
    try {
      return localStorage.getItem(key);
    } catch {
      /* private-mode/storage-disabled browsers: fall through to memory */
    }
  }
  return memoryStore.get(key) ?? null;
}

function storageSet(key: string, value: string): void {
  if (hasLocalStorage()) {
    try {
      localStorage.setItem(key, value);
      return;
    } catch {
      /* fall through to memory */
    }
  }
  memoryStore.set(key, value);
}

/**
 * The same raw id every time this phone reads it — this tab, a new one, or after a reload.
 * Generated once with `crypto.randomUUID()` (no personal information in it — hashing on the server
 * is defence in depth, not the whole security story, per device-id.js's own comment) and persisted
 * forever after. Deliberately no module-level cache variable: every call reads storage fresh, so
 * "persisted" is actually exercised (not just "the same in-memory value was never overwritten").
 */
export function getDeviceId(): string {
  const existing = storageGet(DEVICE_ID_KEY);
  if (existing) return existing;
  const fresh = crypto.randomUUID();
  storageSet(DEVICE_ID_KEY, fresh);
  return fresh;
}

/** AC-REQ-4: "enter singer name (remembered in localStorage)" — null the first time this phone
 * ever requests a song. */
export function getRememberedSingerName(): string | null {
  return storageGet(SINGER_NAME_KEY);
}

export function setRememberedSingerName(name: string): void {
  storageSet(SINGER_NAME_KEY, name);
}

/**
 * MVP-471. What this phone got for passing Turnstile at the join gate: an opaque, server-signed
 * string that `POST /api/requests` checks instead of asking for a second challenge
 * (`strapi-template/src/utils/guest-pass.js`). Stored beside the device id it is bound to —
 * copying it to another phone does nothing there, and it expires on the server twelve hours after
 * it was issued whatever this browser still has in storage.
 *
 * It lives here rather than in its own module for exactly that binding: a pass without the device
 * id it was minted for is meaningless, and two modules each owning half of one fact is how they
 * drift. Absent on every deployment that has not configured Turnstile, which is the default.
 */
export function getGuestPass(): string | null {
  return storageGet(GUEST_PASS_KEY);
}

export function setGuestPass(pass: string): void {
  storageSet(GUEST_PASS_KEY, pass);
}

/** Called when the server refuses a pass (expired, or issued under a rotated secret), so the page
 * goes back to the widget instead of retrying a string that will never work again. */
export function clearGuestPass(): void {
  if (hasLocalStorage()) {
    try {
      localStorage.removeItem(GUEST_PASS_KEY);
    } catch {
      /* fall through to memory */
    }
  }
  memoryStore.delete(GUEST_PASS_KEY);
}
